import React from 'react';
import { useProgressBar } from './ProgressBar.hooks';
import {
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressInfoContainer,
  StepText,
  PercentageText,
} from './ProgressBar.styles';

export interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  progressPercentage?: number;
  variant?: 'default' | 'small' | 'large';
  color?: 'default' | 'success' | 'warning' | 'error';
  showInfo?: boolean;
  className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  progressPercentage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variant = 'default',
  color = 'default',
  showInfo = true,
  className,
}) => {
  const {
    progress,
    isValidStep,
    currentStep: validCurrentStep,
    totalSteps: validTotalSteps,
  } = useProgressBar({
    currentStep,
    totalSteps,
  });
  const resolvedProgress =
    typeof progressPercentage === 'number'
      ? Math.max(0, Math.min(100, progressPercentage))
      : progress;

  // Si los pasos no son válidos, no renderizar nada
  if (!isValidStep) {
    return null;
  }

  // Seleccionar el componente de relleno según el color
  const ProgressFillComponent =
    color === 'success'
      ? ProgressBarFill
      : color === 'warning'
        ? ProgressBarFill
        : color === 'error'
          ? ProgressBarFill
          : ProgressBarFill;

  return (
    <ProgressBarContainer className={className}>
      <ProgressBarTrack>
        <ProgressFillComponent
          style={{ width: `${resolvedProgress}%` }}
          aria-valuenow={resolvedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
          aria-label={`Progreso: ${resolvedProgress}% completado`}
        />
      </ProgressBarTrack>

      {showInfo && (
        <ProgressInfoContainer>
          <StepText variant="caption">
            Paso {validCurrentStep} de {validTotalSteps}
          </StepText>
          <PercentageText variant="caption">{resolvedProgress}% completado</PercentageText>
        </ProgressInfoContainer>
      )}
    </ProgressBarContainer>
  );
};

export default ProgressBar;
